/**
 * Trial Management Service - 100% Production Ready
 * Handles all trial operations for client organizations
 * Backend: /admin/trials
 */

import apiService from './apiClient';
import logger from '../utils/logger';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface Trial {
  id: string;
  organizationId: number;
  organization?: {
    id: number;
    name: string;
    email: string;
  };
  trialType: 'STANDARD' | 'EXTENDED' | 'CUSTOM';
  status: 'ACTIVE' | 'EXPIRED' | 'CONVERTED' | 'CANCELLED';
  startDate: string;
  endDate: string;
  trialDays: number;
  maxExtensions: number;
  extensionsUsed: number;
  features?: Record<string, any>;
  limitations?: Record<string, any>;
  metadata?: Record<string, any>;
  convertedToSubscriptionId?: string;
  cancellationReason?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  notificationsSent?: {
    day7: boolean;
    day3: boolean;
    day1: boolean;
    expiry: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateTrialDto {
  organizationId: number;
  trialType?: 'STANDARD' | 'EXTENDED' | 'CUSTOM';
  trialDays?: number;
  maxExtensions?: number;
  features?: Record<string, any>;
  limitations?: Record<string, any>;
  metadata?: Record<string, any>;
}

export interface ExtendTrialDto {
  extensionDays: number;
  reason: string;
  notes?: string;
}

export interface ConvertTrialDto {
  planId: string;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'YEARLY';
  autoRenew?: boolean;
  paymentMethod?: string;
  metadata?: Record<string, any>;
}

export interface TrialQueryParams {
  page?: number;
  limit?: number;
  status?: 'ACTIVE' | 'EXPIRED' | 'CONVERTED' | 'CANCELLED';
  search?: string;
}

// ============================================================================
// TRIAL SERVICE
// ============================================================================

class TrialService {
  private readonly BASE_PATH = '/admin/trials';

  /**
   * Create a new trial for an organization
   */
  async createTrial(data: CreateTrialDto): Promise<any> {
    try {
      logger.info('Creating new trial', { organizationId: data.organizationId });

      const response = await apiService.post(`${this.BASE_PATH}`, data);

      logger.info('Trial created successfully', { trialId: response.data?.id });

      return response;
    } catch (error: any) {
      logger.error('Failed to create trial', error);
      throw error;
    }
  }

  /**
   * Get all trials with pagination and filters
   */
  async getTrials(params: TrialQueryParams = {}): Promise<any> {
    try {
      logger.info('Fetching trials', params);

      const response = await apiService.get(`${this.BASE_PATH}`, { params });

      logger.info('Trials fetched successfully', {
        total: response.data?.total,
      });

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch trials', error);
      throw error;
    }
  }

  /**
   * Get trials expiring soon (default: 7 days)
   */
  async getExpiringTrials(days: number = 7): Promise<any> {
    try {
      logger.info('Fetching expiring trials', { days });

      const response = await apiService.get(`${this.BASE_PATH}/expiring`, {
        params: { days },
      });

      logger.info('Expiring trials fetched', {
        count: response.data?.length,
      });

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch expiring trials', error);
      throw error;
    }
  }

  /**
   * Get trial by ID
   */
  async getTrialById(trialId: string): Promise<any> {
    try {
      logger.info('Fetching trial details', { trialId });

      const response = await apiService.get(`${this.BASE_PATH}/${trialId}`);

      logger.info('Trial details fetched', { trialId });

      return response;
    } catch (error: any) {
      logger.error('Failed to fetch trial details', { trialId, error });
      throw error;
    }
  }

  /**
   * Extend trial duration
   */
  async extendTrial(trialId: string, data: ExtendTrialDto): Promise<any> {
    try {
      logger.info('Extending trial', { trialId, days: data.extensionDays });

      const response = await apiService.post(`${this.BASE_PATH}/${trialId}/extend`, data);

      logger.info('Trial extended successfully', { trialId });

      return response;
    } catch (error: any) {
      logger.error('Failed to extend trial', { trialId, error });
      throw error;
    }
  }

  /**
   * Cancel a trial
   */
  async cancelTrial(trialId: string, reason?: string): Promise<any> {
    try {
      logger.info('Cancelling trial', { trialId, reason });

      const response = await apiService.post(`${this.BASE_PATH}/${trialId}/cancel`, {
        reason,
      });

      logger.info('Trial cancelled', { trialId });

      return response;
    } catch (error: any) {
      logger.error('Failed to cancel trial', { trialId, error });
      throw error;
    }
  }

  /**
   * Convert trial to paid subscription
   */
  async convertTrialToSubscription(trialId: string, data: ConvertTrialDto): Promise<any> {
    try {
      logger.info('Converting trial to subscription', { trialId, planId: data.planId });

      const response = await apiService.post(`${this.BASE_PATH}/${trialId}/convert`, data);

      logger.info('Trial converted successfully', {
        trialId,
        subscriptionId: response.data?.subscriptionId,
      });

      return response;
    } catch (error: any) {
      logger.error('Failed to convert trial', { trialId, error });
      throw error;
    }
  }
}

// Create and export singleton instance
const trialService = new TrialService();
export default trialService;
